import { Component, OnInit, OnDestroy, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { EnumPipe } from "src/app/shared/pipes";
import { LoaderService, RequestService, LayoutUtilsService } from "src/app/shared/services";
import { CaseService } from "src/app/shared/services/case.service";
import { SignalingService } from "src/app/shared/services/signaling.service";
import { environment } from "src/environments/environment";
import { getOrganizationTemplateDisplayName } from "src/app/shared/constants/template-options";
import { openCenteredPopup } from "src/app/shared/helpers";

@Component({
  selector: 'app-threat-dispatch-dialog',
  templateUrl: './dispatch-dialog.component.html',
  styleUrls: ['./dispatch-dialog.component.scss']
})
export class ThreatDispatchDialogComponent implements OnInit, OnDestroy {

  public caseId: string = '';
  public excludeFromMap: boolean = false;
  public investigatorIds: string[] = [];
  public cases: any[] = [];
  public investigators: any[] = [];
  public loading: boolean = true;
  public investigatorSearch: string = '';
  public ranksEnum: any[] = [];
  public distanceUnit: string = 'km';
  // public camId: string;
  public threat: any;
  public threatOriginal: any;
  public images: string[] = [];
  /** Selected `threats_meta` document ids (slideshow / detection frames). */
  public threatsMetaIds: string[] = [];
  public showThreatManagementCenter: boolean = false;
  public dispatched: boolean = false;
  public dummyThreat: boolean = false;
  private subscriptions: any = [];

  constructor(public dialogRef: MatDialogRef<ThreatDispatchDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any, private loaderService: LoaderService, private requestService: RequestService, private layoutUtilsService: LayoutUtilsService, private translate: TranslateService, private caseService: CaseService, private signalingService: SignalingService, private enumPipe: EnumPipe) {
    this.threat = data.threat;
    this.threatOriginal = JSON.parse(JSON.stringify(data.threat));
    // this.camId = this.threat.cameras[0]?._id;
    this.threatsMetaIds = data.threatsMetaIds || [];
    this.showThreatManagementCenter = data.showThreatManagementCenter || false;
    this.dispatched = this.threat?.threat_status === 'dispatched' || false;
    this.dummyThreat = data.dummyThreat || false;
  }

  /** Resolve case id from threat payload (`caseId`, `case._id`, or string `case`). */
  private resolveThreatCaseId(threat: any = this.threat): string {
    if (!threat) {
      return '';
    }
    const direct = String(threat.caseId || '').trim();
    if (direct) {
      return direct;
    }
    const c = threat.case;
    if (typeof c === 'string') {
      return c.trim();
    }
    return String(c?._id || '').trim();
  }

  private applyResolvedCaseId(caseId: string): void {
    const normalized = String(caseId || '').trim();
    this.caseId = normalized;
    if (normalized && this.threat) {
      this.threat.caseId = normalized;
    }
  }

  ngOnInit(): void {
    const resolvedCaseId = this.resolveThreatCaseId();
    if (resolvedCaseId) {
      this.applyResolvedCaseId(resolvedCaseId);
      this.getInvestigators(resolvedCaseId);
    } else {
      const cameraId = String(
        this.threat?.cameras?.[0]?._id
        || this.threat?.camera_id?._id
        || this.threat?.camera_id
        || ''
      ).trim();
      if (!cameraId) {
        return;
      }
      this.loaderService.display(true);
      this.caseService.getCasesForCamera(cameraId).then((cases: any) => {
        this.cases = cases;
      }).finally(() => {
        this.loaderService.display(false);
      });
    }

    this.subscriptions.push(this.caseService.userMetadata.subscribe((metadata) => {
      if (metadata) {
        this.ranksEnum = metadata.fields.find(i => i.name == 'rank')?.enum;
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  createThreat(type: string) {
    if (!this.dummyThreat) {
      this.handleCreatedThreat(type);
      return;
    }
    this.loaderService.display(true);
    const preservedCaseId = this.caseId || this.resolveThreatCaseId();
    this.requestService.postRequest('threatsmeta', '', this.threat, (data, error) => {
      if (data) {
        // this.layoutUtilsService.showNotification(this.translate.instant('Created successfully'), this.translate.instant('Dismiss'));
        this.threat = data.results;
        if (preservedCaseId) {
          this.applyResolvedCaseId(preservedCaseId);
        }
        this.dummyThreat = false;
        if (type != 'dispatch') {
          this.layoutUtilsService.showNotification(this.translate.instant('Created successfully'), this.translate.instant('Dismiss'));
        }
        this.handleCreatedThreat(type);
      }
      if (error) {
        this.layoutUtilsService.showNotification(this.translate.instant('An error has occurred. Please try again later.'), this.translate.instant('Dismiss'))
        this.loaderService.display(false);
      }
    });
  }
  handleCreatedThreat(type: string) {
    if (type == 'dispatch') {
      let threatsMetaObject = this.threat.threats_meta || [];

      const resolvedCaseId = this.caseId || this.resolveThreatCaseId();
      if (resolvedCaseId) {
        this.applyResolvedCaseId(resolvedCaseId);
      }
      this.threat['geoLocation'] = this.threatOriginal.geolocation;
      this.threat['geolocation'] = this.threatOriginal.geolocation;
      this.threat['address'] = this.threatOriginal.address;
      this.threat['location'] = this.threatOriginal.location;
      this.threat['source_type'] = this.threatOriginal.source_type;
      this.threat['sourceType'] = this.threatOriginal.source_type;
      const resolvedType = this.resolveThreatTypeValue();
      if (resolvedType) {
        this.threat['threat_type'] = resolvedType;
        this.threat['type'] = resolvedType;
      }
      this.threatsMetaIds = threatsMetaObject.map((t) => String(t._id)).filter((id) => id.length > 0);

      this.dispatch();
    } else {
      this.loaderService.display(false);
      this.openThreatManagementCenter();
    }
  }


  async dispatch() {
    const geoLocation = await this.resolveDispatchGeolocation(
      this.caseId,
      this.threatOriginal.geolocation || this.threat.geoLocation || this.threat.geolocation
    );
    if (!geoLocation) {
      return;
    }

    const resolvedLocation = this.resolveDispatchLocation();
    const dispatchName = this.resolveDispatchNoteName();

    this.loaderService.display(true);

    let investigatorIds = this.investigatorIds.map((_id) => {
      return {
        _id: _id,
        name: ''
      }
    });
    let mainPayload = {
      investigators: investigatorIds,
      payload: {
        type: 'dispatched-threat',
        description: this.threatOriginal.description || this.threat.description,
        threats_group_id: this.threat.threats_group_id || this.threat._id || this.threat.threatId,
        threats_meta: this.threatsMetaIds,
        // streamPath: environment.streaming + '/play.html?id=' + this.threat.threatStreamId,
        caseId: this.caseId,
        location: resolvedLocation,
        address: resolvedLocation,
        geoLocation,
        geolocation: geoLocation,
        name: dispatchName,
        title: getOrganizationTemplateDisplayName(this.requestService.pageOrganization.getValue()),
        message: `${this.translate.instant('Incident Detected')}: ${this.resolveThreatTypeLabel()} ${this.translate.instant('at')} ${resolvedLocation}`,
        sourceType: this.threatOriginal.source_type || this.threat.source_type || this.threat.sourceType,
        source_type: this.threatOriginal.source_type || this.threat.source_type,
        excludeFromMap: this.excludeFromMap,
      },
      location: resolvedLocation,
      geolocation: geoLocation,
    }
    // console.log(this.threat, this.images)
    this.requestService.postRequest('notify', 'investigator/send', mainPayload, (data, error) => {
      if (data) {
        this.loaderService.display(false);
        this.sendSignal();
        this.layoutUtilsService.showNotification(this.translate.instant('Dispatched to investigators'), this.translate.instant('Dismiss'));
        // this.dialogRef.close(this.caseId);
        this.dispatched = true;
      }
      else if (error) {
        this.layoutUtilsService.showNotification(this.translate.instant('An error has occurred. Please try again later.'), this.translate.instant('Dismiss'));
        this.loaderService.display(false);
      }
    });
  }

  private parseGeoLocation(geolocation: any): any | null {
    if (!geolocation) {
      return null;
    }

    if (typeof geolocation === 'object') {
      return geolocation;
    }

    if (typeof geolocation === 'string') {
      try {
        return JSON.parse(geolocation);
      } catch {
        return null;
      }
    }

    return null;
  }

  /** Prefer `threats_meta[].threat_type` (selected meta, else first), then top-level threat_type. */
  private resolveThreatTypeValue(): string {
    const threat = this.threat || {};
    const original = this.threatOriginal || {};
    const metaList = Array.isArray(threat.threats_meta)
      ? threat.threats_meta
      : (Array.isArray(original.threats_meta) ? original.threats_meta : []);

    let meta: any;
    if (this.threatsMetaIds?.length && metaList.length) {
      const selectedIds = new Set(this.threatsMetaIds.map((id) => String(id)));
      meta = metaList.find((m: any) => selectedIds.has(String(m?._id || '')));
    }
    if (!meta && metaList.length > 0) {
      meta = metaList[0];
    }

    const raw = meta?.threat_type ?? threat.threat_type ?? original.threat_type;
    if (raw == null || raw === '') {
      return '';
    }
    if (typeof raw === 'object') {
      return String(raw.value || raw.displayName || raw.name || '').trim();
    }
    return String(raw).trim();
  }

  private resolveThreatTypeLabel(): string {
    const typeValue = this.resolveThreatTypeValue();
    if (!typeValue) {
      return '';
    }
    const label = this.enumPipe.transform(typeValue, this.data?.threatTypes);
    return String(label || typeValue).trim();
  }

  /** Prefer a real incident title; for auto-threats use the threat type label (e.g. Face Match). */
  private resolveDispatchNoteName(): string {
    const raw = String(this.threatOriginal?.name || this.threat?.name || '').trim();
    const typeLabel = this.resolveThreatTypeLabel();
    const genericNames = new Set([
      '',
      'Threat Detected',
      'Incident Detected',
      this.translate.instant('Threat Detected'),
      this.translate.instant('Incident Detected'),
    ]);
    if (raw && !genericNames.has(raw)) {
      return raw;
    }
    return typeLabel || raw;
  }

  private resolveDispatchLocation(): string {
    const camera =
      this.threatOriginal?.cameras?.[0]
      || this.threat?.cameras?.[0]
      || (typeof this.threatOriginal?.camera_id === 'object' ? this.threatOriginal.camera_id : null)
      || (typeof this.threat?.camera_id === 'object' ? this.threat.camera_id : null);
    return String(
      this.threatOriginal?.location
      || this.threat?.location
      || this.threatOriginal?.address
      || this.threat?.address
      || camera?.location
      || camera?.address
      || ''
    ).trim();
  }

  getInvestigators(caseId: string, geolocation: string = this.threat.geoLocation ?? this.threat.geolocation) {
    this.loading = true;
    this.loaderService.display(true);
    let settings = this.requestService.getSettings(this.requestService.pageOrganization.getValue());
    this.distanceUnit = settings.distanceUnit;

    void this.resolveInvestigatorGeolocation(caseId, geolocation).then((parsedGeolocation) => {
      if (!parsedGeolocation) {
        this.investigators = [];
        return;
      }
      return this.caseService.getSortedInvestigatorsByDistance(caseId, parsedGeolocation).then((investigators: any) => {
        investigators.forEach((investigator: any) => {
          investigator.distance = this.distanceUnit == 'km' ? investigator.distanceInKm : investigator.distanceInMiles;
        });
        this.investigators = investigators;
      });
    }).finally(() => {
      this.loading = false;
      this.loaderService.display(false);
    });
  }

  private async resolveInvestigatorGeolocation(caseId: string, geolocation?: any): Promise<any | null> {
    let parsed = this.parseGeoLocation(geolocation);
    if (parsed) {
      return parsed;
    }

    const caseFromList = this.cases.find((c) => c._id === caseId);
    parsed = this.parseGeoLocation(caseFromList?.geolocation ?? caseFromList?.geoLocation);
    if (parsed) {
      return parsed;
    }

    try {
      const caseData: any = await this.caseService.getCase(caseId, true);
      return this.parseGeoLocation(caseData?.geolocation ?? caseData?.geoLocation);
    } catch {
      return null;
    }
  }

  private async resolveDispatchGeolocation(caseId: string, geolocation?: any): Promise<any | null> {
    if (this.parseGeoLocation(geolocation)) {
      return geolocation;
    }

    const caseFromList = this.cases.find((c) => c._id === caseId);
    const caseGeo = caseFromList?.geolocation ?? caseFromList?.geoLocation;
    if (this.parseGeoLocation(caseGeo)) {
      return caseGeo;
    }

    try {
      const caseData: any = await this.caseService.getCase(caseId, true);
      const fetchedGeo = caseData?.geolocation ?? caseData?.geoLocation;
      if (this.parseGeoLocation(fetchedGeo)) {
        return fetchedGeo;
      }
    } catch {
      return null;
    }

    return null;
  }

  get filteredInvestigators() {
    if (!this.investigatorSearch) {
      return this.investigators;
    }
    const search = this.investigatorSearch.toLowerCase();
    return this.investigators.filter(investigator =>
      (investigator.alternative_name && investigator.alternative_name.toLowerCase().includes(search)) ||
      (investigator.rank && investigator.rank.toLowerCase().includes(search))
    );
  }

  private sendSignal() {
    this.signalingService.publish('threat', JSON.stringify({
      threatId: this.threat._id,
      threatGroupId: this.threat.threats_group_id || undefined,
      dispatchedBy: this.requestService.currentUser._id,
      type: 'dispatch'
    }), this.signalingService.weaponDetectedTopicName);
  }

  getSelectedInvestigatorsText(): string {
    if (!this.investigatorIds || !this.investigatorIds.length) {
      return '';
    }
    const selected = this.investigatorIds.map(id => this.investigators.find(i => i._id === id)).filter(i => !!i);
    return selected.map(i => {
      let text = i.alternative_name || i.name;
      if (i.rank) {
        text += ` - ${this.enumPipe.transform(i.rank, this.ranksEnum)}`;
      }
      return text;
    }).join(', ');
  }

  openThreatManagementCenter() {
    let url = '/threat/' + this.threat._id;
    if (this.caseId) {
      url += '?caseId=' + this.caseId;
      this.closeDialog(this.caseId);
    }
    openCenteredPopup(url, this.translate.instant('Detection Management Center'));
  }
  closeDialog(data: any = undefined) {
    this.dialogRef.close({ data: data, dummyThreat: this.dummyThreat });
  }
}